import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import HeaderTailwind from "../components/generic/HeaderTailwind";
import FooterTailwind from "../components/generic/FooterTailwind";
import participantsService from "../services/participantsService";
import nationsService from "../services/nationsService";
import categoriesService from "../services/categoriesService";
import groupsService from "../services/groupsService";
import resultsService from "../services/resultsService";
import { medalTableService } from "../services/medalTableService";
import { mvpTableService } from "../services/mvpTableService";
import settingsService from "../services/settingsService";

function AdminTailwindDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    participants: 0,
    nations: 0,
    categories: 0,
    groups: 0,
    eventDuration: "0 Tage",
    results: 0,
    medals: { gold: 0, silver: 0, bronze: 0 },
    topMVPScore: 0
  });

  useEffect(() => {
    const loadStatistics = async () => {
      try {
        setLoading(true);
        
        // Lade alle Statistiken parallel
        const [
          participantsResult,
          nationsResult,
          categoriesResult,
          groupsResult,
          resultsResult,
          medalTableResult,
          mvpTableResult,
          settingsResult
        ] = await Promise.all([
          participantsService.getAllParticipants(),
          nationsService.getAllNations(),
          categoriesService.getAllCategories(),
          groupsService.getAllGroups(),
          resultsService.getAllResults(),
          medalTableService.getMedalTable().catch(() => ({ nations: [] })),
          mvpTableService.getMVPTable().catch(() => ({})),
          settingsService.getAllSettings()
        ]);

        // Berechne Statistiken
        const participantsCount = participantsResult.success ? participantsResult.data.length : 0;
        const nationsCount = nationsResult.success ? nationsResult.data.length : 0;
        const categoriesCount = categoriesResult.success ? categoriesResult.data.length : 0;
        const groupsCount = groupsResult.success ? groupsResult.data.length : 0;
        const resultsCount = resultsResult.success ? resultsResult.data.length : 0;

        // Medaillen zählen
        let medals = { gold: 0, silver: 0, bronze: 0 };
        if (medalTableResult && Array.isArray(medalTableResult.nations)) {
          medalTableResult.nations.forEach(nation => {
            medals.gold += nation.gold || 0;
            medals.silver += nation.silver || 0;
            medals.bronze += nation.bronze || 0;
          });
        }

        // Höchster MVP-Score finden
        let topMVPScore = 0;
        if (mvpTableResult && typeof mvpTableResult === 'object') {
          Object.values(mvpTableResult).forEach(ageGroupData => {
            if (Array.isArray(ageGroupData) && ageGroupData.length > 0) {
              const maxScore = Math.max(...ageGroupData.map(p => p.totalScore || 0));
              if (maxScore > topMVPScore) {
                topMVPScore = maxScore;
              }
            }
          });
        }

        // Event-Dauer berechnen
        let eventDuration = "0 Tage";
        if (settingsResult.success && settingsResult.data) {
          const startDate = settingsResult.data.competition_start_date?.value;
          const endDate = settingsResult.data.competition_end_date?.value;
          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 um Start- und Endtag einzubeziehen
            eventDuration = `${diffDays} ${diffDays === 1 ? 'Tag' : 'Tage'}`;
          }
        }

        setStats({
          participants: participantsCount,
          nations: nationsCount,
          categories: categoriesCount,
          groups: groupsCount,
          eventDuration,
          results: resultsCount,
          medals,
          topMVPScore: topMVPScore.toFixed(2)
        });
      } catch (error) {
        console.error('Fehler beim Laden der Statistiken:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, []);

  const getAdminCards = () => [
    {
      title: t("admin.dashboard.participants", "Teilnehmer"),
      description: t("admin.dashboard.participantsDesc", "Teilnehmer verwalten und bearbeiten"),
      icon: "fas fa-users",
      color: "blue",
      onClick: () => navigate("/admin/participants"),
      statValue: stats.participants,
      statLabel: t("admin.dashboard.participants", "Teilnehmer")
    },
    {
      title: t("admin.dashboard.nations", "Nationen"),
      description: t("admin.dashboard.nationsDesc", "Nationen verwalten und bearbeiten"),
      icon: "fas fa-flag",
      color: "green",
      onClick: () => navigate("/admin/nations"),
      statValue: stats.nations,
      statLabel: t("admin.dashboard.nations", "Nationen")
    },
    {
      title: t("admin.dashboard.categories", "Kategorien"),
      description: t("admin.dashboard.categoriesDesc", "Kategorien verwalten"),
      icon: "fas fa-list",
      color: "purple",
      onClick: () => navigate("/admin/categories"),
      statValue: stats.categories,
      statLabel: t("admin.dashboard.categories", "Kategorien")
    },
    {
      title: t("admin.dashboard.groups", "Gruppen"),
      description: t("admin.dashboard.groupsDesc", "Gruppen und Kategorien verwalten"),
      icon: "fas fa-layer-group",
      color: "indigo",
      onClick: () => navigate("/admin/groups"),
      statValue: stats.groups,
      statLabel: t("admin.dashboard.groups", "Gruppen")
    },
    {
      title: t("admin.dashboard.schedule", "Zeitplan"),
      description: t("admin.dashboard.scheduleDesc", "Wettkampfzeitplan verwalten"),
      icon: "fas fa-calendar-alt",
      color: "orange",
      onClick: () => navigate("/admin/schedule"),
      statValue: stats.eventDuration,
      statLabel: t("admin.dashboard.eventDuration", "Event-Dauer")
    },
    {
      title: t("admin.dashboard.results", "Ergebnisse"),
      description: t("admin.dashboard.resultsDesc", "Wettkampfergebnisse verwalten"),
      icon: "fas fa-clipboard-list",
      color: "teal",
      onClick: () => navigate("/admin/results"),
      statValue: stats.results,
      statLabel: t("admin.dashboard.results", "Ergebnisse")
    },
    {
      title: t("admin.dashboard.medals", "Medaillenspiegel"),
      description: t("admin.dashboard.medalsDesc", "Medaillenspiegel und Nationen-Rankings"),
      icon: "fas fa-medal",
      color: "yellow",
      onClick: () => navigate("/admin/medal-table"),
      statValue: `${stats.medals.gold} / ${stats.medals.silver} / ${stats.medals.bronze}`,
      statLabel: t("admin.dashboard.medals", "Medaillen"),
      isMedal: true
    },
    {
      title: t("admin.dashboard.mvp", "MVP-Tabelle"),
      description: t("admin.dashboard.mvpDesc", "Beste Athleten je Altersgruppe"),
      icon: "fas fa-trophy",
      color: "amber",
      onClick: () => navigate("/admin/mvp-table"),
      statValue: stats.topMVPScore > 0 ? stats.topMVPScore : "-",
      statLabel: t("admin.dashboard.topMVPScore", "Höchster MVP-Score")
    },
    {
      title: t("admin.dashboard.settings", "Einstellungen"),
      description: t("admin.dashboard.settingsDesc", "Zeitplan-Konfiguration und Systemeinstellungen"),
      icon: "fas fa-cog",
      color: "gray",
      onClick: () => navigate("/admin/settings"),
      statValue: null,
      statLabel: null
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-900 transition-colors">
      <HeaderTailwind />
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {t("admin.dashboard.title", "Admin Dashboard")}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            {t("admin.dashboard.subtitle", "Verwalte alle Aspekte der Veranstaltung.")}
          </p>
        </div>

        {/* Dashboard Cards with Statistics */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="flex items-start gap-4 mb-4">
                  {/* Icon Skeleton */}
                  <div className="w-12 h-12 rounded-lg bg-slate-200 dark:bg-slate-700 flex-shrink-0"></div>
                  {/* Title and Stat Skeleton */}
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                    </div>
                  </div>
                </div>
                {/* Description Skeleton */}
                <div className="space-y-2 mb-4">
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-5/6"></div>
                </div>
                {/* Footer Skeleton */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                  <div className="w-4 h-4 bg-slate-200 dark:bg-slate-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getAdminCards().map((card, index) => (
              <div
                key={index}
                onClick={card.onClick}
                className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all cursor-pointer group hover:border-primary dark:hover:border-primary-light"
              >
                <div className="p-6">
                  {/* Icon, Title and Statistic */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      card.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                      card.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                      card.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' :
                      card.color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' :
                      card.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' :
                      card.color === 'teal' ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400' :
                      card.color === 'gray' ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' :
                      card.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' :
                      'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                    } group-hover:scale-110 transition-transform`}>
                      <i className={`${card.icon} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 group-hover:text-primary dark:group-hover:text-primary-light transition-colors mb-1">
                        {card.title}
                      </h3>
                      {card.statValue !== null && card.statLabel && (
                        <div className="mt-2">
                          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                            {card.statLabel}
                          </div>
                          {card.isMedal ? (
                            <div className="text-base font-bold text-slate-900 dark:text-slate-100">
                              <span className="text-yellow-600 dark:text-yellow-400">{stats.medals.gold}</span>
                              {" / "}
                              <span className="text-gray-400 dark:text-gray-500">{stats.medals.silver}</span>
                              {" / "}
                              <span className="text-orange-600 dark:text-orange-400">{stats.medals.bronze}</span>
                            </div>
                          ) : (
                            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                              {card.statValue}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    {card.description}
                  </p>

                  {/* Arrow */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {t("admin.dashboard.clickToOpen", "Klicken zum Öffnen")}
                    </div>
                    <div className="text-slate-400 dark:text-slate-500 group-hover:text-primary dark:group-hover:text-primary-light group-hover:translate-x-1 transition-all">
                      <i className="fas fa-chevron-right text-sm"></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Actions Section */}
        <div className="mt-12 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
            {t("admin.dashboard.quickActions", "Schnellaktionen")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate("/admin/participants")}
              className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-blue-200 dark:border-blue-800"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <i className="fas fa-user-plus text-blue-600 dark:text-blue-400"></i>
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {t("admin.dashboard.addParticipant", "Teilnehmer hinzufügen")}
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400">
                  {t("admin.dashboard.newParticipant", "Neuen Teilnehmer registrieren")}
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/admin/schedule")}
              className="flex items-center gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-lg transition-colors border border-orange-200 dark:border-orange-800"
            >
              <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <i className="fas fa-calendar-plus text-orange-600 dark:text-orange-400"></i>
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-medium text-orange-900 dark:text-orange-100">
                  {t("admin.dashboard.generateSchedule", "Zeitplan generieren")}
                </div>
                <div className="text-xs text-orange-600 dark:text-orange-400">
                  {t("admin.dashboard.autoSchedule", "Automatischen Zeitplan erstellen")}
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/admin/results")}
              className="flex items-center gap-3 p-4 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30 rounded-lg transition-colors border border-teal-200 dark:border-teal-800"
            >
              <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                <i className="fas fa-clipboard-check text-teal-600 dark:text-teal-400"></i>
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-medium text-teal-900 dark:text-teal-100">
                  {t("admin.dashboard.enterResults", "Ergebnisse eingeben")}
                </div>
                <div className="text-xs text-teal-600 dark:text-teal-400">
                  {t("admin.dashboard.addResults", "Wettkampfergebnisse erfassen")}
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-info-circle text-blue-600 dark:text-blue-400 text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                {t("admin.dashboard.welcome", "Willkommen im Admin-Bereich")}
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                {t("admin.dashboard.welcomeText", "Hier können Sie alle Aspekte der Veranstaltung verwalten. Nutzen Sie die Karten oben, um zu den verschiedenen Verwaltungsbereichen zu gelangen.")}
              </p>
              <div className="flex items-center gap-6 text-xs text-blue-600 dark:text-blue-400">
                <div className="flex items-center gap-2">
                  <i className="fas fa-check-circle"></i>
                  <span>{t("admin.dashboard.ready", "Bereit")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fas fa-users"></i>
                  <span>{t("admin.dashboard.participants", "Teilnehmer")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fas fa-calendar"></i>
                  <span>{t("admin.dashboard.schedule", "Zeitplan")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
      <FooterTailwind />
    </div>
  );
}

export default AdminTailwindDashboard;